'use client';

import { useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import CustomModal from '@/components/common/modal/modal';
import CustomButton from '@/components/common/buttons/button';
import classes from './recaptcha_v2_modal.module.css';

/**
 * reCAPTCHA v2 Modal Component
 * 
 * Megjelenik, amikor a v3 score túl alacsony és v2 challenge szükséges.
 * 
 * @param {boolean} show - Modal megjelenítése
 * @param {function} onClose - Modal bezárása (X gomb / ESC)
 * @param {function} onSuccess - Sikeres v2 challenge callback (token-nel)
 */
export default function RecaptchaV2Modal({ show, onClose, onSuccess }) {
    const recaptchaRef = useRef(null);

    // Site key environment változóból
    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY;

    useEffect(() => {
        // Modal bezárásakor reset
        if (!show && recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
    }, [show]);

    const handleRecaptchaChange = (token) => {
        if (token) {
            // Sikeres v2 challenge
            onSuccess(token);
            
            // Reset a következő alkalomra
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();
            }
        }
    };

    const handleRecaptchaExpired = () => {
        // Token lejárt, reset
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
    };

    const handleRecaptchaError = () => {
        // reCAPTCHA hiba (network, timeout, stb.)
        console.error('reCAPTCHA v2 error occurred');
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
    };

    if (!siteKey) {
        // If the site key is missing, the component renders nothing, which looks like "modal doesn't open".
        console.error('NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY is not set (RecaptchaV2Modal will not render)');
        return null;
    }

    if (show) {
        // eslint-disable-next-line no-console
        console.debug('[recaptcha-v2] modal requested to show');
    }

    return (
        <CustomModal
            show={show}
            close={onClose}
            type="warning"
            title="Biztonsági ellenőrzés szükséges"
            buttons={[
                <CustomButton
                    key="close"
                    variant="secondary"
                    onClick={onClose}
                >
                    Mégse
                </CustomButton>
            ]}
        >
            <div className={classes.content}>
                <p className="text-sm-regular color-gray-600 margin-bottom-16">
                    A biztonsági rendszerünk gyanús aktivitást észlelt. 
                    Kérjük, igazold, hogy nem vagy robot a lenti feladat megoldásával.
                </p>
                
                <div className={classes.recaptchaContainer}>
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={siteKey}
                        onChange={handleRecaptchaChange}
                        onExpired={handleRecaptchaExpired}
                        onErrored={handleRecaptchaError}
                        theme="light"
                        size="normal"
                    />
                </div>

                <p className="text-xs-regular color-gray-500 margin-top-12">
                    A feladat megoldása után automatikusan folytatódik az üzenet küldése.
                </p>
            </div>
        </CustomModal>
    );
}
